// Store Picker: store chooser and store change for the storefront.
// Data comes from the #store-picker-data JSON rendered by snippets/store-picker.liquid.
(() => {
  // Product pages: if the page opened on an option the chosen store can't sell,
  // switch to the first one it can (the theme's picker re-renders the section).
  // Also covers quick buy drawers, whose product form arrives after page load.
  const preferSellableOptions = (root) => {
    root.querySelectorAll?.('variant-picker').forEach((picker) => {
      const preferred = picker.querySelector('script[data-sp-preferred]');
      if (!preferred || preferred.dataset.spApplied || !picker.selectCombination) return;
      preferred.dataset.spApplied = 'true';
      picker.selectCombination({ optionValues: JSON.parse(preferred.textContent), productChange: false });
    });
  };
  window.customElements?.whenDefined('variant-picker').then(() => {
    preferSellableOptions(document);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach(preferSellableOptions));
    }).observe(document.body, { childList: true, subtree: true });
  });

  const STORAGE_KEY = 'store_picker_store';
  const overlay = document.getElementById('store-picker');
  const dataElement = document.getElementById('store-picker-data');
  if (!overlay || !dataElement) return;

  const data = JSON.parse(dataElement.textContent);
  const storeByHandle = new Map(data.stores.map((store) => [store.handle, store]));
  const chooseView = overlay.querySelector('[data-sp-view="choose"]');
  const confirmView = overlay.querySelector('[data-sp-view="confirm"]');
  const errorMessage = overlay.querySelector('[data-sp-error]');
  let pendingHandle = null;

  const readSaved = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  };
  const save = (handle) => {
    try {
      localStorage.setItem(STORAGE_KEY, handle);
    } catch {
      // Private browsing: the cart attribute still holds the choice.
    }
  };

  // Preferred Pricing (Portal) tier members: Quick Order and the website shop
  // one shared store, and a member may be limited to certain stores (one store
  // = locked to it). null for everyone else, who can shop any store.
  // { locked, allowed: Set of handles | null, active: handle | null, quickOrderInProgress }
  let access = null;
  let accessReady = Promise.resolve();
  const ACCESS_KEY = 'store_picker_access';
  const NOTICE_KEY = 'store_picker_notice';
  const PORTAL_URL = `${window.Shopify?.routes?.root ?? '/'}apps/preferred-pricing`;

  const isAllowed = (handle) => !access?.allowed || access.allowed.has(handle);
  const handleForLocation = (locationId) =>
    locationId ? data.stores.find((store) => String(store.locationId) === String(locationId))?.handle ?? null : null;

  const portal = async (body) => {
    const response = await fetch(PORTAL_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.error || `Portal request failed (${response.status})`);
    return result;
  };

  const applyAccess = (next) => {
    access = next;
    overlay.querySelectorAll('[data-sp-store]').forEach((element) => {
      element.hidden = !isAllowed(element.dataset.spStore);
    });
    document.documentElement.classList.toggle('sp-locked', Boolean(access?.locked));
    try {
      localStorage.setItem(ACCESS_KEY, JSON.stringify({ customer: data.customer, locked: Boolean(access?.locked) }));
    } catch {
      // Only used to hide Change store before the page paints.
    }
  };

  const open = () => {
    if (access?.locked && storeByHandle.has(data.current)) return;
    overlay.querySelectorAll('.sp-view').forEach((view) => {
      view.hidden = view !== chooseView;
    });
    errorMessage.hidden = true;
    overlay.hidden = false;
    overlay.querySelector('button')?.focus();
  };
  const close = () => {
    // The chooser cannot be closed until a store is chosen.
    if (data.current && storeByHandle.has(data.current) && isAllowed(data.current)) overlay.hidden = true;
  };

  // Saves the store on the cart, removes the given cart lines, and reloads so
  // every page shows that store's products.
  // Members: the Portal saves the store first (it cancels their Quick Order if
  // the store changes), so a store they aren't allowed never reaches the cart.
  const switchTo = async (handle, removals, { shareWithPortal = true } = {}) => {
    if (access && shareWithPortal && handle !== access.active) {
      await portal({ intent: 'set-store-location', locationId: storeByHandle.get(handle)?.locationId });
    }
    const updates = Object.fromEntries(removals.map((item) => [item.key, 0]));
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, attributes: { selected_store: handle } }),
    });
    if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
    save(handle);
    try {
      // Line notes name the old store's limits.
      sessionStorage.removeItem('store_picker_line_notes');
    } catch {
      // Nothing to clear.
    }
    await preferPickupAtStore(handle);
    window.location.reload();
  };

  // Reads the cart as it is now, not as it was when the page loaded, so items
  // added in another tab are checked too.
  const fetchCartItems = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}?sections=store-picker-cart`);
    if (!response.ok) throw new Error(`Cart check failed (${response.status})`);
    const html = (await response.json())['store-picker-cart'];
    const script = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-store-picker-cart]');
    const live = JSON.parse(script.textContent);
    // Another tab may have changed the store since this page loaded.
    data.current = live.current;
    return live.items;
  };

  const unavailableAt = (items, handle) =>
    items.filter((item) => !(item.stores ?? []).includes(handle));

  const choose = async (handle) => {
    if (handle === data.current) return close();
    await accessReady;
    if (!isAllowed(handle)) return;

    const removals = unavailableAt(await fetchCartItems(), handle);
    const cancelsQuickOrder = Boolean(access?.quickOrderInProgress && handle !== access.active);
    if (removals.length > 0 || cancelsQuickOrder) {
      pendingHandle = handle;
      confirmView.querySelector('[data-sp-quick-order-warning]').hidden = !cancelsQuickOrder;
      confirmView.querySelector('[data-sp-removal-block]').hidden = removals.length === 0;
      confirmView.querySelector('[data-sp-confirm]').textContent =
        removals.length > 0 ? 'Remove items and switch' : 'Switch store';
      const name = storeByHandle.get(handle)?.name ?? handle;
      confirmView.querySelectorAll('[data-sp-new-name]').forEach((element) => {
        element.textContent = name;
      });
      const list = confirmView.querySelector('[data-sp-removals]');
      list.replaceChildren(
        ...removals.map((item) => {
          const line = document.createElement('li');
          line.textContent = describe(item);
          return line;
        }),
      );
      chooseView.hidden = true;
      confirmView.hidden = false;
      confirmView.querySelector('[data-sp-confirm]').focus();
      return;
    }

    await switchTo(handle, []);
  };

  // Variant names often repeat the product name ("HELL RAISER ... (Case)"),
  // so show just the variant then.
  const itemName = (item) => {
    const variant = item.variant && item.variant !== 'Default Title' ? item.variant.trim() : '';
    if (!variant) return item.title;
    const base = item.title.trim().toLowerCase();
    return variant.toLowerCase().startsWith(base) ? variant : `${item.title} (${variant})`;
  };
  const describe = (item) => `${itemName(item)} × ${item.quantity}`;

  // Before checkout: the cart must belong to a chosen store and every item must
  // be sellable there. Reads the live cart, so changes in other tabs count.
  const checkBeforeCheckout = async () => {
    const items = await fetchCartItems();
    if (!storeByHandle.has(data.current)) {
      open();
      return false;
    }
    const removals = unavailableAt(items, data.current);
    if (removals.length === 0) return checkQuantities(items);

    overlay.querySelectorAll('[data-sp-current-name]').forEach((element) => {
      element.textContent = storeByHandle.get(data.current).name;
    });
    overlay.querySelector('[data-sp-checkout-removals]').replaceChildren(
      ...removals.map((item) => {
        const line = document.createElement('li');
        line.textContent = describe(item);
        return line;
      }),
    );
    overlay.querySelectorAll('.sp-view').forEach((view) => {
      view.hidden = view.dataset.spView !== 'checkout';
    });
    errorMessage.hidden = true;
    overlay.hidden = false;
    overlay.querySelector('[data-sp-remove-unavailable]').focus();
    return false;
  };

  // Checkout picks its own default pickup location. Tell it the chosen store
  // through the cart's delivery preference (Storefront API, no token needed
  // for carts). Customers can still change it at checkout; the app's order
  // check flags that. Failure here never blocks checkout.
  const preferPickupAtStore = async (handle = data.current) => {
    const store = storeByHandle.get(handle);
    if (!store?.locationId) return;
    // Checkout lists pickup stores nearest first, so the store's own
    // coordinates put it at the top even if the handle is not used.
    const delivery = { deliveryMethod: ['PICK_UP'], pickupHandle: [String(store.locationId)] };
    if (store.latitude != null && store.longitude != null) {
      delivery.coordinates = { latitude: store.latitude, longitude: store.longitude, countryCode: 'US' };
    }
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const cart = await (await fetch(`${root}cart.js`)).json();
      await fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: `mutation storePickerPickup($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
            cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
              userErrors { message }
            }
          }`,
          variables: {
            cartId: `gid://shopify/Cart/${cart.token}`,
            buyerIdentity: { preferences: { delivery } },
          },
        }),
      });
    } catch {
      // Checkout still works; the customer picks the store there.
    }
  };

  // Shopify only offers pickup at a store with enough of every line, and
  // otherwise silently switches the whole order to another store. So ask the
  // Store Picker app how many the chosen store can sell. At checkout, lines
  // that ask for more are lowered automatically and the customer is told; on
  // product pages the quantity box stops at the limit. If the app can't be
  // reached, nothing is blocked.
  const limitCache = new Map();

  const fetchLimits = async (variantIds) => {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}apps/store-picker/limits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ store: data.current, variants: variantIds }),
    });
    if (!response.ok) return null;
    const limits = (await response.json()).limits ?? null;
    if (limits) Object.entries(limits).forEach(([id, limit]) => limitCache.set(id, limit));
    return limits;
  };

  const checkQuantities = async (items) => {
    let limits = null;
    try {
      limits = await fetchLimits(items.map((item) => item.variantId));
    } catch {
      limits = null;
    }
    if (!limits) return true;

    const overLimit = items
      .map((item) => ({ ...item, limit: limits[String(item.variantId)] }))
      .filter((item) => typeof item.limit === 'number' && item.quantity > item.limit);
    if (overLimit.length === 0) return true;

    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(overLimit.map((item) => [item.key, item.limit])),
      }),
    });
    if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

    overlay.querySelectorAll('[data-sp-current-name]').forEach((element) => {
      element.textContent = storeByHandle.get(data.current).name;
    });
    overlay.querySelector('[data-sp-quantity-list]').replaceChildren(
      ...overLimit.map((item) => {
        const line = document.createElement('li');
        line.textContent =
          item.limit > 0
            ? `${itemName(item)}: ${item.quantity} → ${item.limit}`
            : `${itemName(item)}: removed, none available right now`;
        return line;
      }),
    );
    overlay.querySelectorAll('.sp-view').forEach((view) => {
      view.hidden = view.dataset.spView !== 'quantity';
    });
    errorMessage.hidden = true;
    overlay.hidden = false;
    overlay.querySelector('[data-sp-continue-checkout]').focus();
    return false;
  };

  // Preferred Pricing members check out with the Portal's own cart button
  // (it turns the cart into a draft order). Held here until the cart passes.
  const PORTAL_CHECKOUT = '.ssp-cart-summary__button';
  let heldPortalButton = null;
  const releasePortalButton = (button) => {
    heldPortalButton = null;
    overlay.hidden = true;
    button.dataset.spChecked = 'true';
    button.click();
  };

  const continueToCheckout = async () => {
    if (heldPortalButton?.isConnected) return releasePortalButton(heldPortalButton);
    await preferPickupAtStore();
    window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
  };

  // Product pages and quick buy: cap the quantity box at the store's limit.
  const productForms = () => Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
  // getAttribute, not form.id: Shopify's product form has a field named "id"
  // (the variant), which shadows the form's own id property.
  const quantityInputFor = (form) => {
    const formId = form.getAttribute('id');
    return (
      (formId && document.querySelector(`input[name="quantity"][form="${CSS.escape(formId)}"]`)) ||
      form.querySelector('input[name="quantity"]')
    );
  };

  // Cart page and drawer: each line's quantity box (data-line-key) is capped
  // too. Lines already over the limit are lowered right away and get a note
  // under the item, like Shopify's own "Only N were added" message. Notes
  // live in sessionStorage because the cart page reloads after each change,
  // keyed by variant: Shopify gives a line a new key when its quantity changes.
  const NOTES_KEY = 'store_picker_line_notes';
  const lineVariant = new Map(data.items.map((item) => [item.key, String(item.variantId)]));
  const readNotes = () => {
    try {
      return JSON.parse(sessionStorage.getItem(NOTES_KEY) || '{}');
    } catch {
      return {};
    }
  };
  const writeNotes = (notes) => {
    try {
      sessionStorage.setItem(NOTES_KEY, JSON.stringify(notes));
    } catch {
      // Notes are only a courtesy.
    }
  };
  const lineNote = (limit) => `Only ${limit} available at ${storeByHandle.get(data.current)?.name ?? 'your store'}.`;
  const showLineNotes = () => {
    const notes = readNotes();
    document.querySelectorAll('input[data-line-key]').forEach((input) => {
      const text = notes[lineVariant.get(input.dataset.lineKey)];
      const info = (input.closest('tr') ?? input.closest('.line-item'))?.querySelector('.line-item__info');
      if (!text || !info || info.querySelector('[data-sp-line-note]')) return;
      const note = document.createElement('p');
      note.className = 'text-with-icon text-error text-xs';
      note.setAttribute('role', 'alert');
      note.dataset.spLineNote = '';
      note.innerHTML =
        '<svg width="14" viewBox="0 0 18 18" aria-hidden="true"><path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path><path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path></svg>';
      note.append(` ${text}`);
      info.append(note);
    });
  };
  let loweringLines = false;
  const applyLineLimits = async () => {
    const inputs = Array.from(document.querySelectorAll('input[data-line-key]'));
    if (inputs.length === 0) return;
    if (inputs.some((input) => !lineVariant.has(input.dataset.lineKey))) {
      (await fetchCartItems()).forEach((item) => lineVariant.set(item.key, String(item.variantId)));
    }
    const missing = [...new Set(inputs.map((input) => lineVariant.get(input.dataset.lineKey)))].filter(
      (id) => id && !limitCache.has(id),
    );
    if (missing.length > 0 && !(await fetchLimits(missing))) return;

    const over = new Map();
    const overVariants = new Map();
    inputs.forEach((input) => {
      const variantId = lineVariant.get(input.dataset.lineKey);
      const limit = limitCache.get(variantId);
      // 0 = the store can't sell it at all; the checkout check removes those.
      if (typeof limit !== 'number' || limit < 1) return;
      input.max = String(limit);
      if (Number(input.value) > limit) {
        over.set(input.dataset.lineKey, limit);
        overVariants.set(variantId, limit);
      }
    });
    showLineNotes();
    if (over.size === 0 || loweringLines) return;

    loweringLines = true;
    const notes = readNotes();
    overVariants.forEach((limit, variantId) => {
      notes[variantId] = lineNote(limit);
    });
    writeNotes(notes);
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: Object.fromEntries(over) }),
    });
    if (data.pageType === 'cart') {
      window.location.reload();
      return;
    }
    loweringLines = false;
    if (response.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  // The theme quietly trims a typed quantity down to the box's max as you
  // type, and the up arrow just stops at it, so say why as it happens.
  const noteAtLimit = (input) => {
    const variantId = lineVariant.get(input.dataset.lineKey);
    const limit = limitCache.get(variantId);
    if (typeof limit !== 'number' || limit < 1) return;
    const notes = readNotes();
    notes[variantId] = lineNote(limit);
    writeNotes(notes);
    showLineNotes();
  };
  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!input.matches?.('input[data-line-key]')) return;
      const limit = limitCache.get(lineVariant.get(input.dataset.lineKey));
      if (typeof limit === 'number' && Number(input.value) > limit) noteAtLimit(input);
    },
    true,
  );
  document.addEventListener(
    'keydown',
    (event) => {
      const input = event.target;
      if (event.key !== 'ArrowUp' || !input.matches?.('input[data-line-key]')) return;
      const limit = limitCache.get(lineVariant.get(input.dataset.lineKey));
      if (typeof limit === 'number' && Number(input.value) >= limit) noteAtLimit(input);
    },
    true,
  );

  // Typing or pressing + past the limit in the cart: lower it before the
  // theme sends the change (this runs first, on the way down).
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input.matches?.('input[data-line-key]')) return;
      const notes = readNotes();
      const variantId = lineVariant.get(input.dataset.lineKey);
      const limit = limitCache.get(variantId);
      if (typeof limit === 'number' && limit >= 1 && Number(input.value) > limit) {
        input.value = String(limit);
        notes[variantId] = lineNote(limit);
      } else if (typeof limit !== 'number' || Number(input.value) < limit) {
        // Lowered below the limit by the shopper: the note no longer applies.
        delete notes[variantId];
      }
      writeNotes(notes);
    },
    true,
  );

  const applyQuantityLimits = async () => {
    if (!storeByHandle.has(data.current)) return;
    try {
      await applyLineLimits();
    } catch {
      loweringLines = false;
    }
    const forms = productForms()
      .map((form) => ({ form, variantId: form.querySelector('[name="id"]')?.value, input: quantityInputFor(form) }))
      .filter((entry) => entry.variantId && entry.input);
    const missing = [...new Set(forms.map((entry) => entry.variantId))].filter((id) => !limitCache.has(id));
    if (missing.length > 0) {
      try {
        await fetchLimits(missing);
      } catch {
        return;
      }
    }
    forms.forEach(({ variantId, input }) => {
      const limit = limitCache.get(String(variantId));
      if (typeof limit !== 'number' || limit < 1) return;
      input.max = String(limit);
      if (Number(input.value) > limit) input.value = String(limit);
    });
  };

  // The page changes constantly (countdown timers, apps, animations), so a
  // run is scheduled once and never pushed back by further changes.
  let limitTimer = null;
  const scheduleQuantityLimits = () => {
    if (limitTimer) return;
    limitTimer = setTimeout(() => {
      limitTimer = null;
      applyQuantityLimits();
    }, 250);
  };
  scheduleQuantityLimits();
  // Variant changes re-render the product form and quick buy drawers load later.
  new MutationObserver(scheduleQuantityLimits).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('input[name="quantity"], [name="id"], input[data-option-position]')) {
      scheduleQuantityLimits();
    }
  });
  // Never send more than the limit, even if typed in.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches?.('form[action*="/cart/add"]')) return;
      const input = quantityInputFor(form);
      const limit = limitCache.get(String(form.querySelector('[name="id"]')?.value));
      if (input && typeof limit === 'number' && limit > 0 && Number(input.value) > limit) {
        input.value = String(limit);
      }
    },
    true,
  );

  const removeUnavailable = async () => {
    await switchTo(data.current, unavailableAt(await fetchCartItems(), data.current));
  };

  const run = (task) =>
    task().catch(() => {
      errorMessage.hidden = false;
    });

  overlay.addEventListener('click', (event) => {
    const chooseButton = event.target.closest('[data-sp-choose]');
    if (chooseButton) return run(() => choose(chooseButton.dataset.spChoose));
    if (event.target.closest('[data-sp-confirm]')) {
      // Check the live cart again: it may have changed while the warning showed.
      return run(async () =>
        switchTo(pendingHandle, unavailableAt(await fetchCartItems(), pendingHandle)),
      );
    }
    if (event.target.closest('[data-sp-cancel]')) return open();
    if (event.target.closest('[data-sp-remove-unavailable]')) return run(removeUnavailable);
    if (event.target.closest('[data-sp-continue-checkout]')) return run(continueToCheckout);
    if (event.target.closest('[data-sp-review-cart]')) return window.location.reload();
    if (event.target.closest('[data-sp-close]')) return close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) close();
  });

  // Checkout buttons (cart page and drawer) submit a form with name="checkout".
  // Hold the submit, check the cart, then continue the same submit if it passes.
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (submitter?.name !== 'checkout' || event.target.dataset.spChecked) return;
      event.preventDefault();
      run(async () => {
        if (!(await checkBeforeCheckout())) return;
        await preferPickupAtStore();
        const form = event.target;
        form.dataset.spChecked = 'true';
        const marker = document.createElement('input');
        marker.type = 'hidden';
        marker.name = 'checkout';
        form.append(marker);
        form.submit();
      });
    },
    true,
  );

  // The Portal's checkout button (tier members): its click handler is on the
  // button itself, so stopping the click here on the way down holds it.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(PORTAL_CHECKOUT);
      if (!button) return;
      if (button.dataset.spChecked) {
        delete button.dataset.spChecked;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      heldPortalButton = button;
      run(async () => {
        if (await checkBeforeCheckout()) releasePortalButton(button);
      });
    },
    true,
  );

  // Plain links straight to checkout get the same check.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href*="/checkout"]');
      if (!link) return;
      event.preventDefault();
      run(async () => {
        if (!(await checkBeforeCheckout())) return;
        await preferPickupAtStore();
        window.location.href = link.href;
      });
    },
    true,
  );

  // Delegated: product sections are re-rendered when options change.
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-sp-open]')) open();
  });

  // Collections: always filter to the chosen store with the Search & Discovery
  // "Available stores" filter, so counts, pages and sorting stay correct. Links
  // from menus don't carry the filter, so add it here. The search page doesn't
  // support this filter (results would be empty), so it is removed there and
  // the theme hides unsellable products instead. Cases-only Preferred Pricing
  // members filter by "Case stores" instead (data.storeFilter).
  const STORE_FILTERS = ['filter.p.m.store_picker.available_stores', 'filter.p.m.store_picker.case_stores'];
  const STORE_FILTER = data.storeFilter ?? STORE_FILTERS[0];
  if (data.pageType === 'search' && STORE_FILTERS.some((name) => new URL(window.location.href).searchParams.has(name))) {
    const url = new URL(window.location.href);
    STORE_FILTERS.forEach((name) => url.searchParams.delete(name));
    window.location.replace(url.toString());
  }
  if (data.pageType === 'collection' && storeByHandle.has(data.current)) {
    const url = new URL(window.location.href);
    const applied = url.searchParams.getAll(STORE_FILTER);
    const other = STORE_FILTERS.some((name) => name !== STORE_FILTER && url.searchParams.has(name));
    if (other || applied.length !== 1 || applied[0] !== data.current) {
      STORE_FILTERS.forEach((name) => url.searchParams.delete(name));
      url.searchParams.append(STORE_FILTER, data.current);
      url.searchParams.delete('page');
      window.location.replace(url.toString());
    }
  }

  // After a member was moved to their account's store: say so, and list any
  // cart items that were removed because that store can't sell them.
  const showNotice = () => {
    let notice = null;
    try {
      notice = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || 'null');
      sessionStorage.removeItem(NOTICE_KEY);
    } catch {
      return;
    }
    if (!notice || notice.handle !== data.current) return;
    const noticeView = overlay.querySelector('[data-sp-view="notice"]');
    overlay.querySelectorAll('[data-sp-current-name]').forEach((element) => {
      element.textContent = storeByHandle.get(data.current)?.name ?? data.current;
    });
    // Members locked to one store get the Preferred Pricing wording; Birmingham
    // is the main warehouse for bulk purchasing.
    noticeView.querySelector('[data-sp-notice-shared]').hidden = Boolean(notice.locked);
    noticeView.querySelector('[data-sp-notice-locked]').hidden = !notice.locked;
    noticeView.querySelector('[data-sp-notice-bulk]').hidden = notice.handle !== 'ssfw-birmingham';
    noticeView.querySelector('[data-sp-notice-removal-block]').hidden = notice.removed.length === 0;
    noticeView.querySelector('[data-sp-notice-removals]').replaceChildren(
      ...notice.removed.map((text) => {
        const line = document.createElement('li');
        line.textContent = text;
        return line;
      }),
    );
    overlay.querySelectorAll('.sp-view').forEach((view) => {
      view.hidden = view !== noticeView;
    });
    overlay.hidden = false;
  };

  // Members: follow the store they last chose in Quick Order or on the website
  // (last choice wins), and stay within the stores their account allows.
  // Returns true when the page is about to reload into another store.
  const followPortalStore = async () => {
    const result = await portal({ intent: 'store-location' });
    if (!('locked' in result)) {
      applyAccess(null);
      return false;
    }
    let allowed = result.allowedLocationIds ? new Set(result.allowedLocationIds.map(handleForLocation).filter(Boolean)) : null;
    // Allowed stores that aren't pickup stores here: don't block every store.
    if (allowed && allowed.size === 0) allowed = null;
    applyAccess({
      locked: Boolean(result.locked) && allowed?.size === 1,
      allowed,
      active: handleForLocation(result.activeLocationId),
      quickOrderInProgress: Boolean(result.quickOrderInProgress),
    });

    const target = access.locked ? [...allowed][0] : access.active && isAllowed(access.active) ? access.active : null;
    if (target && target !== data.current) {
      const removals = unavailableAt(await fetchCartItems(), target);
      if (target === data.current) return false;
      try {
        sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ handle: target, locked: access.locked, removed: removals.map(describe) }));
      } catch {
        // The switch still happens; only the explanation is lost.
      }
      await switchTo(target, removals);
      return true;
    }
    if (!target && storeByHandle.has(data.current) && isAllowed(data.current)) {
      // First visit since sharing began: the website's store becomes theirs.
      await portal({ intent: 'set-store-location', locationId: storeByHandle.get(data.current).locationId });
      access.active = data.current;
    }
    return false;
  };

  const start = async () => {
    if (data.customer) {
      accessReady = followPortalStore().catch(() => {
        // Portal unreachable: shop as before rather than block the page.
        access = null;
        return false;
      });
      if (await accessReady) return;
    }
    showNotice();
    // The cart attribute is lost when a cart expires or is cleared. If this
    // browser chose a store before, restore it quietly instead of asking again.
    const saved = readSaved();
    if (!storeByHandle.has(data.current) && storeByHandle.has(saved) && isAllowed(saved) && data.items.length === 0) {
      await switchTo(saved, []);
      return;
    }
    if (storeByHandle.has(data.current) && !isAllowed(data.current)) open();
  };
  run(start);
})();
